/* eslint-disable max-lines */
import { numeralify } from 'common/utils/utils';
import { getDefaultFormatter } from 'common/utils/time.utils';
import _ from 'lodash';
import { getColor } from 'common/constants/_colors';
import {
  calcMetricMetGoalsPercent,
  mapMetricsToStackBarSeries,
  metricMaxPoint
} from './custom-report.serivce';
import { formatVerboseReviewTime } from '../../../settings/views/Notifications/notifications.service';
import { metricNames, SettingValues, GRANULARITY } from '@linearb/linjs-common';
import { getDurationLevels } from '@linearb/linui-utils';

export { GRANULARITY };
export const GRANULARITY_TO_METRICS_V2_GRANULARITY = {
  [GRANULARITY.AUTO]: '1d',
  [GRANULARITY.DAYS]: '1d',
  [GRANULARITY.WEEKS]: '1w',
  [GRANULARITY.MONTHS]: '1mo',
  [GRANULARITY.ITERATIONS]: 'custom'
};
export const DATE_RANGE_FORMAT = 'MMM D';
export const GRAPH_STRATEGIES = {
  REPORTED: 'reported'
};
export const GRANULARITY_OPTIONS = [
  {
    value: GRANULARITY.AUTO,
    displayValue: 'Default'
  },
  {
    value: GRANULARITY.DAYS,
    displayValue: 'Daily'
  },
  {
    value: GRANULARITY.WEEKS,
    displayValue: 'Weekly'
  },
  {
    value: GRANULARITY.MONTHS,
    displayValue: 'Monthly'
  },
  {
    value: GRANULARITY.ITERATIONS,
    displayValue: 'Iterations'
  }
];

export const GRANULARITY_OPTIONS_KANBAN = [
  {
    value: GRANULARITY.AUTO,
    displayValue: 'Default'
  },
  {
    value: GRANULARITY.DAYS,
    displayValue: 'Daily'
  },
  {
    value: GRANULARITY.WEEKS,
    displayValue: 'Weekly'
  },
  {
    value: GRANULARITY.MONTHS,
    displayValue: 'Monthly'
  }
];

export const FORMATTERS = {
  timespan: {
    yFormatter(value, levels = 2) {
      return getDurationLevels({
        minutes: value,
        levels,
        format:
          levels === 2
            ? getDefaultFormatter({ minutes: value })
            : 'D[d], h[h], m[m]',
        ignoreZeros: true
      });
    },
    avgFormatter(avg, levels = 2, isShortUnitName = true) {
      const levelsFormatUnits = isShortUnitName
        ? {}
        : {
            minutesUnit: ' minutes',
            hoursUnit: ' hours',
            daysUnit: ' days',
            yearsUnit: ' years'
          };
      const formatUnits = isShortUnitName
        ? 'D[d], h[h], m[m]'
        : 'D[ days], h[ hours], m[ minutes]';
      return avg > 0
        ? getDurationLevels({
            minutes: avg,
            levels,
            format:
              levels === 2
                ? getDefaultFormatter({ minutes: avg, ...levelsFormatUnits })
                : formatUnits,
            ignoreZeros: true
          })
        : '-';
    }
  },
  count: {
    yFormatter(value) {
      return numeralify(_.round(value, 2));
    },
    avgFormatter(avg, fixedDecimalPlaces) {
      return isNaN(avg) || avg === 0
        ? '-'
        : fixedDecimalPlaces
        ? numeralify(_.round(avg, 2).toFixed(fixedDecimalPlaces))
        : numeralify(_.round(avg, 2));
    }
  },
  nonAvgCount: {
    yFormatter(value) {
      return numeralify(_.round(value, 2));
    },
    avgFormatter(avg, fixedDecimalPlaces) {
      return isNaN(avg) || avg === 0
        ? '-'
        : fixedDecimalPlaces
        ? numeralify(_.round(avg, 2).toFixed(fixedDecimalPlaces))
        : numeralify(_.round(avg, 2));
    }
  },
  percentage: {
    yFormatter(value) {
      return `${numeralify(_.round(value, 2))}%`;
    },
    avgFormatter(avg, fixedDecimalPlaces) {
      return isNaN(avg) || avg === 0
        ? '-'
        : fixedDecimalPlaces
        ? `${numeralify(_.round(avg, 2).toFixed(fixedDecimalPlaces))}%`
        : `${numeralify(_.round(avg, 2))}%`;
    }
  },
  percentageWholeNumber: {
    yFormatter(value) {
      return `${numeralify(_.round(value, 0))}%`;
    },
    avgFormatter(avg) {
      return isNaN(avg) || avg === 0 ? '-' : `${numeralify(_.round(avg, 0))}%`;
    }
  }
};

export const FILTER_BY = {
  PEOPLE: 'People',
  REPO: 'Repository',
  SERVICE: 'Service',
  LABEL: 'PR Label'
};

export const GROUP_BY_KEY = {
  TEAM: 'team',
  GROUP: 'group',
  LABEL: 'label'
};

export const GRAPH_TO_ICON = {
  bar: 'barchart',
  line: 'AreaChart'
};

export const GRAPH_TYPES = {
  BAR: 'bar',
  LINE: 'line',
  SPLINE: 'spline',
  AREA_SPLINE: 'areaspline',
  STACKED: 'stacked'
};

export const DEFAULT_GRAPHS = [GRAPH_TYPES.BAR, GRAPH_TYPES.LINE];

export const METRICS_GOALS = {
  MET_GOALS: {
    text: 'Met goal',
    color: getColor('catskillWhite')
  },
  DID_NOT_MEET_GOALS: {
    text: 'Did not meet goal',
    color: getColor('orangePeel')
  }
};

/* istanbul ignore next */
export const METRICS_METADATA = {
  27: {
    order: {
      teamGoals: 1
    },
    isTeamGoals: true,
    settingTitle: 'Pull Request Size',
    setGoalText:
      'Help your team break down their pull requests into smaller, more digestible chunks.',
    description: ({ pr_size }) => (
      <>
        Goal: Avoid merging pull requests
        <br />
        with more than {pr_size} code changes
      </>
    ),
    didNotMeetTooltipDescription: ({ pr_size }) =>
      `PRs with more than ${pr_size} code changes`,
    annotationTooltip: ({ oldValue = {}, newValue = {} } = {}) =>
      `Goal changed from ${oldValue.pr_size} code changes to ${newValue.pr_size} code changes`,
    goalsValues: metrics =>
      calcMetricMetGoalsPercent(
        metrics,
        metricNames.MERGED_LARGE_PRS,
        metricNames.PR_MERGED
      ),
    settingName: SettingValues.PULL_REQUESTS_LARGER_THAN,
    axisTitle: 'Merged PRs',
    stackGraphMapper: metrics =>
      mapMetricsToStackBarSeries(
        metrics,
        metricNames.MERGED_LARGE_PRS,
        metricNames.PR_MERGED
      ),
    maxPoint: metrics => metricMaxPoint(metrics, metricNames.PR_MERGED)
  },
  28: {
    order: {
      teamGoals: 4
    },
    isTeamGoals: true,
    settingTitle: 'Pull Request Lifecycle',
    setGoalText:
      'Track and improve the time it takes for a pull request to be merged.',
    description: ({ review_time }) =>
      `Goal: Merge pull requests in less than ${formatVerboseReviewTime(
        review_time
      )}`,
    didNotMeetTooltipDescription: ({ review_time }) =>
      `PRs merged after more than ${formatVerboseReviewTime(review_time)}`,
    annotationTooltip: ({ oldValue = {}, newValue = {} } = {}) =>
      `Goal changed from ${formatVerboseReviewTime(
        oldValue.review_time
      )} to ${formatVerboseReviewTime(newValue.review_time)} `,
    goalsValues: metrics =>
      calcMetricMetGoalsPercent(
        metrics,
        metricNames.MERGED_LONG_LIVING_PRS,
        metricNames.PR_MERGED
      ),
    settingName: SettingValues.PULL_REQUESTS_OPEN_MORE_THAN,
    axisTitle: 'Merged PRs',
    stackGraphMapper: metrics =>
      mapMetricsToStackBarSeries(
        metrics,
        metricNames.MERGED_LONG_LIVING_PRS,
        metricNames.PR_MERGED
      ),
    maxPoint: metrics => metricMaxPoint(metrics, metricNames.PR_MERGED)
  },
  // 29: {
  //   isTeamGoals: true,
  //   settingTitle: 'Link all pull request to tickets',
  //   setGoalText: '',
  //   description: () => `Team Goal: All PRs should be linked to tickets`,
  //   goalsValues: metrics =>
  //     calcMetricMetGoalsPercent(
  //       metrics,
  //       metricNames.MERGED_UNLINKED_PRS,
  //       metricNames.PR_MERGED
  //     ),
  //   settingName: SettingValues.UNLINKED_BRANCHES,
  //   axisTitle: 'Branches',
  //   stackGraphMapper: metrics =>
  //     mapMetricsToStackBarSeries(
  //       metrics,
  //       metricNames.MERGED_UNLINKED_PRS,
  //       metricNames.PR_MERGED
  //     ),
  //   maxPoint: metrics => metricMaxPoint(metrics, metricNames.PR_MERGED)
  // },
  30: {
    order: {
      teamGoals: 2
    },
    isTeamGoals: true,
    settingTitle: 'Pull Request Pickup Time',
    setGoalText:
      'Decrease the amount of time it takes for a pull request to be picked up for review.',
    description: ({ review_time }) => (
      <>
        Goal: Start pull request reviews within
        <br />
        {formatVerboseReviewTime(review_time)} after being issued
      </>
    ),
    didNotMeetTooltipDescription: ({ review_time }) =>
      `PRs merged after waiting for review for more than ${formatVerboseReviewTime(
        review_time
      )}`,
    annotationTooltip: ({ oldValue = {}, newValue = {} } = {}) =>
      `Goal changed from ${formatVerboseReviewTime(
        oldValue.review_time
      )} to ${formatVerboseReviewTime(newValue.review_time)}`,
    goalsValues: metrics =>
      calcMetricMetGoalsPercent(
        metrics,
        metricNames.MERGED_REVIEW_REQUEST_HANGING,
        metricNames.PR_MERGED
      ),
    settingName: SettingValues.REVIEW_REQUEST_HANGING_NOTIFICATION,
    axisTitle: 'Merged PRs',
    stackGraphMapper: metrics =>
      mapMetricsToStackBarSeries(
        metrics,
        metricNames.MERGED_REVIEW_REQUEST_HANGING,
        metricNames.PR_MERGED
      ),
    maxPoint: metrics => metricMaxPoint(metrics, metricNames.PR_MERGED)
  },
  31: {
    order: {
      teamGoals: 5
    },
    isTeamGoals: true,
    settingTitle: 'Pull Requests Merged without Review',
    setGoalText:
      'Reduce the number of pull requests that are merged without review.',
    description: ({ pr_size }) => (
      <>
        <>Goal: Do not merge pull requests with more than</>
        <br />
        <>{pr_size} code changes if it does not have a review</>
      </>
    ),
    didNotMeetTooltipDescription: () => `PRs merged without review`,
    annotationTooltip: ({ oldValue = {}, newValue = {} } = {}) =>
      `Goal changed from ${oldValue.pr_size} code changes to ${newValue.pr_size} code changes`,
    goalsValues: metrics =>
      calcMetricMetGoalsPercent(
        metrics,
        metricNames.MERGED_WITHOUT_REVIEW,
        metricNames.PR_MERGED
      ),
    settingName: SettingValues.MERGED_WITHOUT_REVIEW_NOTIFICATION,
    axisTitle: 'Merged PRs',
    stackGraphMapper: metrics =>
      mapMetricsToStackBarSeries(
        metrics,
        metricNames.MERGED_WITHOUT_REVIEW,
        metricNames.PR_MERGED
      ),
    maxPoint: metrics => metricMaxPoint(metrics, metricNames.PR_MERGED)
  },
  32: {
    order: {
      teamGoals: 7
    },
    isTeamGoals: true,
    settingTitle: 'High Risk Work',
    setGoalText:
      'Set this goal to notify the team when a large pull request has a high refactor/rework rate.',
    description: ({ pr_size, rework_refactor }) => (
      <>
        <>Goal: Identify pull requests that have more than {pr_size} code</>
        <br />
        <>changes and a refactor or rework rate of {rework_refactor}% or more</>
      </>
    ),
    didNotMeetTooltipDescription: () => `PRs merged containing work at risk`,
    annotationTooltip: ({ oldValue = {}, newValue = {} } = {}) =>
      `Goal changed from ${oldValue.pr_size} code changes and ${oldValue.rework_refactor}% rework / refactor to ${newValue.pr_size} code changes and ${newValue.rework_refactor}% rework / refactor`,
    goalsValues: metrics =>
      calcMetricMetGoalsPercent(
        metrics,
        metricNames.MERGED_WORK_AT_RISK_PRS,
        metricNames.PR_MERGED
      ),
    settingName: SettingValues.WORK_AT_RISK_NOTIFICATION,
    axisTitle: 'Merged PRs',
    stackGraphMapper: metrics =>
      mapMetricsToStackBarSeries(
        metrics,
        metricNames.MERGED_WORK_AT_RISK_PRS,
        metricNames.PR_MERGED
      ),
    maxPoint: metrics => metricMaxPoint(metrics, metricNames.PR_MERGED)
  },
  33: {
    order: {
      teamGoals: 6
    },
    isTeamGoals: true,
    settingTitle: 'Pull Requests Merged with Basic Review',
    setGoalText:
      "When LGTM isn't enough, set a goal to improve the quality of your team's pull request reviews.",
    description: ({ review_time, pr_size }) => (
      <>
        <>Goal: Do not merge pull requests that have more than</>
        <br />
        <>
          {pr_size} code changes and a review time of less than {review_time}{' '}
          minutes
        </>
      </>
    ),
    didNotMeetTooltipDescription: () => `PRs merged with basic review`,
    annotationTooltip: ({ oldValue = {}, newValue = {} } = {}) =>
      `Goal changed from ${oldValue.review_time} minutes and ${oldValue.pr_size} code
changes to ${newValue.review_time} minutes and ${newValue.pr_size} code changes`,
    goalsValues: metrics =>
      calcMetricMetGoalsPercent(
        metrics,
        metricNames.MERGED_WITH_BASIC_REVIEW_PRS,
        metricNames.PR_MERGED
      ),
    settingName: SettingValues.MERGED_WITH_BASIC_REVIEW_NOTIFICATION,
    axisTitle: 'Merged PRs',
    stackGraphMapper: metrics =>
      mapMetricsToStackBarSeries(
        metrics,
        metricNames.MERGED_WITH_BASIC_REVIEW_PRS,
        metricNames.PR_MERGED
      ),
    maxPoint: metrics => metricMaxPoint(metrics, metricNames.PR_MERGED)
  },
  34: {
    order: {
      teamGoals: 3
    },
    isTeamGoals: true,
    settingTitle: 'Pull Request Review Time',
    setGoalText:
      'Decrease your pull request review time by setting a goal for your team.',
    description: ({ review_time }) =>
      `Goal: Complete pull request reviews in less than ${formatVerboseReviewTime(
        review_time
      )} `,
    didNotMeetTooltipDescription: ({ review_time }) =>
      `PRs merged with reviews longer than ${formatVerboseReviewTime(
        review_time
      )} `,
    annotationTooltip: ({ oldValue = {}, newValue = {} } = {}) =>
      `Goal changed from ${formatVerboseReviewTime(
        oldValue.review_time
      )} to ${formatVerboseReviewTime(newValue.review_time)} `,
    goalsValues: metrics =>
      calcMetricMetGoalsPercent(
        metrics,
        metricNames.MERGED_LONG_REVIEW_PRS,
        metricNames.PR_MERGED
      ),
    settingName: SettingValues.REVIEW_TOO_LONG_NOTIFICATION,
    axisTitle: 'Merged PRs',
    stackGraphMapper: metrics =>
      mapMetricsToStackBarSeries(
        metrics,
        metricNames.MERGED_LONG_REVIEW_PRS,
        metricNames.PR_MERGED
      ),
    maxPoint: metrics => metricMaxPoint(metrics, metricNames.PR_MERGED)
  }
};

export const TEAM_GOALS_WIDGETS_ORDER = _.orderBy(
  Object.entries(METRICS_METADATA).map(([key, value = []]) => {
    return { key, order: value?.order?.teamGoals };
  }),
  'order'
)
  .filter(({ order }) => order)
  .map(({ key }) => key);

export const EMPTY_DUMMY_ORG_LEVEL_TEAM_GOALS_DASHBOARD = {
  type: 'goalsAllTeams',
  id: 'team-goals-all-teams',
  route: 'team-goals-all-teams',
  name: 'Team Goals is a team view',
  account_id: 0,
  created_at: '2022-02-02T08:50:18.477Z',
  description: null,
  is_public: true,
  internal_type: 'teamGoals',
  definition: {
    is_editable: false,
    widgets: [],
    filters: {},
    is_show_repos_dropdown: false,
    is_show_members_dropdown: false,
    is_show_all_teams: true,
    is_multi_teams_allowed: false,
    max_retention_date: '01/01/2022'
  }
};

export const REPORT_IDS = {
  DELIVERY: 'Delivery',
  QUALITY: 'Quality',
  THROUGHPUT: 'Throughput',
  DORA: 'Dora',
  AI_CODE_REVIEW: 'AI Code Review',
  AI_GITHUB_COPILOT: 'github_copilot'
};

export const DEFAULT_REPORT_IDS = [
  REPORT_IDS.DELIVERY,
  REPORT_IDS.QUALITY,
  REPORT_IDS.THROUGHPUT,
  REPORT_IDS.DORA,
  REPORT_IDS.AI_CODE_REVIEW
];
